// Studio Row's data: who the creators are and what they made, read from the catalogue. Pure (no DOM, no network), so
// both the zone sections and the creator page share it and the tests can run it on the real listing.
//
// Never invent anything about a person: every line here is counted or quoted from their listed games.

import type { Catalogue, Creator, Game, Kind } from "../../catalogue.ts";
import { coverUrl } from "../../rows.ts";

export interface Studio {
  creator: Creator;
  /** their listed games, art first, then the catalogue's order */
  games: Game[];
  /** games with real (not typographic) art */
  realArt: number;
  /** what they make most, for the directory's groups */
  group: Group;
  /**
   * "person": a named person or studio (a handle, a name). "host": we only know where their games are hosted (the
   * catalogue's creator is a domain, `evidence: "site"`) or it's a portal or AI platform (PORTAL_HOSTS). Hosts are
   * never spotlit and sit last in the directory, under "Found on the web".
   */
  who: "person" | "host";
}

export type Group = "games" | "worlds" | "learn";
export const GROUP_LABEL: Record<Group, string> = { games: "Games", worlds: "Worlds and toys", learn: "Lessons and examples" };
/** The directory's last, quiet group: hosts and portals (whether they show at all is the owner's call). */
export const HOSTS_LABEL = "Found on the web";
const KIND_GROUP: Record<Kind, Group> = { game: "games", experience: "worlds", learn: "learn", example: "learn" };

/** Typographic covers (w12-art) carry `artKind: "type"`; real art is the creator's own game, drawn or captured. */
export function hasRealArt(g: Game): boolean {
  return !!coverUrl(g) && (g as { artKind?: unknown }).artKind !== "type";
}

/** Art first: real art, then typographic covers, then none; the catalogue's order inside each. */
export function artFirst(games: readonly Game[]): Game[] {
  const rank = (g: Game) => (hasRealArt(g) ? 0 : coverUrl(g) ? 1 : 2);
  return games.map((g, i) => ({ g, i })).sort((a, b) => rank(a.g) - rank(b.g) || a.i - b.i).map((x) => x.g);
}

/** Every creator with a listed game (first-party Banger games aren't a creator's studio). */
export function studios(cat: Pick<Catalogue, "games" | "creators">): Studio[] {
  const by = new Map<string, Game[]>();
  for (const g of cat.games) {
    if (g.firstParty || g.status === "coming_soon") continue;
    const list = by.get(g.creator.id);
    if (list) list.push(g);
    else by.set(g.creator.id, [g]);
  }
  const named = new Map(cat.creators.map((c) => [c.id, c] as const));
  const out: Studio[] = [];
  for (const [id, games] of by) {
    const creator = named.get(id) ?? games[0]!.creator;
    const sorted = artFirst(games);
    out.push({ creator, games: sorted, realArt: games.filter(hasRealArt).length, group: groupOf(games), who: isHostCreator(creator, games) ? "host" : "person" });
  }
  return out;
}

/**
 * Portals and AI platforms that host other people's games: a rights question (docs/build/AUDIT-W11.md, "40 games are
 * hosted on portals or AI platforms"), so never "a studio". One list, exported for the catalogue and the launcher to
 * share. A host matches itself and its subdomains.
 */
export const PORTAL_HOSTS = [
  "gameer.io",
  "arcadewhale.com",
  "kral.games",
  "rosebud.ai",
  "sekai.ai",
  "crazygames.com",
  "youware.com",
  "youware.app",
  "grok.me",
  "itch.zone",
  "manus.game",
  "manus.space",
  "poki.com",
  "websim.ai",
  "websim.com",
] as const;

/** A creator id built from a host (cr_gameer-io) or a name that is a host (gameer.io, 9pjzm6.csb.app). */
const HOST_NAME = /^(?!@)[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/i;

export function isHostName(name: string): boolean {
  return HOST_NAME.test(name.trim());
}

function hostMatches(host: string): boolean {
  const h = host.toLowerCase().replace(/^www\./, "");
  return PORTAL_HOSTS.some((p) => h === p || h.endsWith(`.${p}`));
}

/** A portal's own account (@gameer_ai): its brand, the host's first label, in the name. */
const BRANDS = [...new Set(PORTAL_HOSTS.map((p) => p.split(".")[0]!).filter((b) => b.length >= 4))];
function portalBrand(name: string): boolean {
  const n = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  return BRANDS.some((b) => n.includes(b.replace(/[^a-z0-9]/g, "")));
}

/** Is this host (or a URL on it) a portal or AI platform? */
export function isPortalHost(hostOrUrl: string | null | undefined): boolean {
  if (!hostOrUrl) return false;
  try {
    return hostMatches(hostOrUrl.includes("://") ? new URL(hostOrUrl).host : hostOrUrl);
  } catch {
    return false;
  }
}

/**
 * Only a host, not a person: the creator's name is a domain, or their name, link or every game sits on a portal.
 * A named person whose one game happens to be on a portal stays a person.
 */
export function isHostCreator(c: Pick<Creator, "name" | "url">, games: readonly Pick<Game, "url">[] = []): boolean {
  if (isHostName(c.name) || isPortalHost(c.name) || isPortalHost(c.url) || portalBrand(c.name)) return true;
  return games.length > 0 && games.every((g) => isPortalHost(g.url)) && !c.name.startsWith("@");
}

/** The group a creator's games fall in most (ties go games, worlds, lessons). */
export function groupOf(games: readonly Pick<Game, "kind">[]): Group {
  const n: Record<Group, number> = { games: 0, worlds: 0, learn: 0 };
  for (const g of games) n[KIND_GROUP[g.kind] ?? "games"]++;
  return (["games", "worlds", "learn"] as const).reduce((best, k) => (n[k] > n[best] ? k : best), "games" as Group);
}

/** Search the directory by creator name or one of their game titles. */
export function searchStudios(list: readonly Studio[], q: string): Studio[] {
  const t = q.trim().toLowerCase().replace(/^@/, "");
  if (!t) return [...list];
  const hit = (s: string) => s.toLowerCase().replace(/^@/, "").includes(t);
  return list.filter((s) => hit(s.creator.name) || s.games.some((g) => hit(g.title)));
}

/** A display name: X handles keep their "@" in links but not as a heading. */
export function displayName(c: Pick<Creator, "name">): string {
  if (isHostName(c.name)) return c.name.toLowerCase().replace(/^www\./, "");
  return c.name.replace(/^@(?=\S)/, "");
}

/** A creator's own link, labelled for people ("x.com/kodub", "github.com/kodub", "kodub.com"); http(s) only. */
export function linkLabel(url: string | null): { href: string; label: string } | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    const host = u.host.replace(/^www\./, "");
    const path = u.pathname.replace(/\/+$/, "");
    return { href: u.href, label: path && path.split("/").length <= 2 ? `${host}${path}` : host };
  } catch {
    return null;
  }
}

/** "3 games" / "1 world" etc.: what they have on Banger, counted. */
export function madeLine(games: readonly Pick<Game, "kind">[]): string {
  const n = games.length;
  const g = groupOf(games);
  const noun = g === "worlds" ? ["world", "worlds"] : g === "learn" ? ["lesson", "lessons"] : ["game", "games"];
  return `${n} ${n === 1 ? noun[0] : noun[1]} on Banger`;
}

/**
 * A spotlight's one line, from the facts: their games' titles (never a made-up bio).
 * 1 game: its blurb. More: "Made Golden Hour Skatepark, Night Drive and 2 more."
 */
export function storyLine(s: Pick<Studio, "games">): string {
  const g = s.games;
  if (g.length === 1) return g[0]!.blurb || `Made ${g[0]!.title}.`;
  const named = g.slice(0, 2).map((x) => x.title);
  const more = g.length - named.length;
  return more > 0 ? `Made ${named.join(", ")} and ${more} more.` : `Made ${named[0]} and ${named[1]}.`;
}

const WEEK = 7 * 86400000;
const MONDAY = 4 * 86400000; // 1 Jan 1970 was a Thursday

/**
 * Spotlights for a week: creators whose studios read best (at least two games with real art), rotated weekly so
 * the row changes without anyone scheduling it. `pinned` (the showrunner's picks, from the spec's section arg) go
 * first. Deterministic for a given `now`.
 */
export function spotlightPicks(list: readonly Studio[], now: number, pinned: readonly string[] = [], n = 3): Studio[] {
  const out: Studio[] = [];
  for (const id of pinned) {
    const s = list.find((x) => x.creator.id === id);
    if (s && s.who === "person" && !out.includes(s)) out.push(s);
  }
  const pool = list.filter((s) => s.who === "person" && s.realArt >= 2 && !out.includes(s)).sort((a, b) => b.realArt - a.realArt || a.creator.id.localeCompare(b.creator.id));
  if (pool.length) {
    const start = (((Math.floor((now - MONDAY) / WEEK) * n) % pool.length) + pool.length) % pool.length; // weeks from Monday 00:00 UTC
    for (let i = 0; out.length < n && i < pool.length; i++) out.push(pool[(start + i) % pool.length]!);
  }
  return out.slice(0, n);
}

/** The Halloween window for "Haunt makers" (docs/programming/crosspromo.md): 23 Oct – 11 Nov 2026, UTC (extended by the owner on 3 Nov). */
export const HAUNT_MAKERS = { from: Date.UTC(2026, 9, 23), to: Date.UTC(2026, 10, 11) };

/** The creators of the Halloween row's games, art first; empty out of season or with fewer than three of them. */
export function hauntMakers(cat: Pick<Catalogue, "games" | "creators" | "rows">, now: number): Studio[] {
  if (now < HAUNT_MAKERS.from || now >= HAUNT_MAKERS.to) return [];
  const row = cat.rows?.find((r) => r.id === "halloween");
  if (!row) return [];
  const ids = new Set(row.games);
  const all = studios(cat);
  const makers = all
    .map((s) => ({ ...s, games: artFirst(s.games.filter((g) => ids.has(g.id))) }))
    .filter((s) => s.who === "person" && s.games.length > 0)
    .sort((a, b) => Number(hasRealArt(b.games[0]!)) - Number(hasRealArt(a.games[0]!)) || b.games.length - a.games.length);
  return makers.length >= 3 ? makers : [];
}

/**
 * The banner's tiles: up to five of their games' art, real art first. A creator with no art at all gets none: the
 * banner falls back to type (their name, set large).
 */
export function bannerTiles(games: readonly Game[]): Game[] {
  return artFirst(games).filter((g) => !!coverUrl(g)).slice(0, 5);
}
